import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  matrixQueries,
  updateMatrixCell,
  type MatrixUi,
  type UpdateMatrixCellInput,
} from "@/domains/matrix";

type UpdateMatrixCellContext = {
  previous?: MatrixUi;
};

// Optimistic, per `docs/mutation-conventions.md`.
export function useUpdateMatrixCell(collectionId: string) {
  const queryClient = useQueryClient();
  const queryKey = matrixQueries.detail(collectionId).queryKey;

  return useMutation<
    { analysis_id: string; column_key: string },
    unknown,
    UpdateMatrixCellInput,
    UpdateMatrixCellContext
  >({
    mutationFn: (input) => updateMatrixCell(collectionId, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MatrixUi>(queryKey);

      if (previous) {
        const isManual = input.value.trim().length > 0;
        const existingCell = previous.rows.find(
          (row) => row.analysisId === input.analysisId,
        )?.cells[input.columnKey] ?? { value: "" };

        queryClient.setQueryData<MatrixUi>(queryKey, {
          ...previous,
          rows: previous.rows.map((row) =>
            row.analysisId === input.analysisId
              ? {
                  ...row,
                  cells: {
                    ...row.cells,
                    [input.columnKey]: {
                      ...existingCell,
                      value: input.value,
                      status: isManual ? "completed" : "idle",
                      source: isManual ? "manual" : undefined,
                    },
                  },
                }
              : row,
          ),
        });
      }

      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries(matrixQueries.detail(collectionId));
      void queryClient.invalidateQueries(matrixQueries.lists());
    },
  });
}
